<template>
    <ac-modal :title="'Collect Debt'" ref="modal">
        <template>
            <div class="row">
                <div class="col-md-12">
                    <p>Debt summary for {{ member }}</p>
                    <p style="font-weight: bold!important;">Total debt: {{ netDebt | currency }}</p>
                    <hr/>
                </div>
            </div>
            <div class="row" v-if="!updateCC">
                <div class="col-md-6 form-group" v-for="dr in debtRecords" :key="dr.consumerDebtId">
                    <ul style="padding: 5px">
                        <li>
                            <input type="checkbox" class="filled-in" :id="'collect-'+dr.consumerDebtId" v-model="dr.isChecked">
                            <label :for="'collect-'+dr.consumerDebtId">
                                Debt for {{ monthName(dr.month) }} <span v-if="dr.year">{{ dr.year }}</span>
                                <span v-if="dr.planCategoryDesc"> &ndash; {{ dr.planCategoryDesc }}</span>
                            </label><br>
                            <span>Amount due to provider: </span>{{ dr.debt | currency }}<br>
                            <span>Other dues : </span>{{ dr.feesDebt | currency }}<br>
                            <span>Reason: </span>{{ dr.reason }}<br>
                        </li>
                    </ul>
                </div>
                <div class="col-md-12">
                    <div class="alert alert-warning">
                        <input type="checkbox" class="filled-in" id='debt-ver-check' v-model="memberAgrees">
                        <label for="debt-ver-check" style="text-align: left">
                            I agree, acknowledge and attest that I have been explicitly 
                            and directly been provided authorization and consent from the
                            member attached to this debt record to collect payment via
                            credit/debit card on {{ new Date() | shortDate }}
                        </label>
                    </div>
                    <div class="col-md-12">
                        <div class="alert alert-info">
                            {{ selectedCardDisplay }}
                        </div>
                    </div>
                    <p>
                        To update the primary member's payment method click 
                        <a style="cursor: pointer" @click.prevent="updateCC=true">here</a>.
                    </p>
                </div>
            </div>
            <div class="row" v-else>
                <div class="col-md-12" v-if="ccWidgetReady">
                    <ac-current-credit-cards v-model="ccVm" ref="currentCCsComponent" :needsSelect="true"
                       @delete="deleteCreditCard" @default="defaultCreditCard" @save="saveCreditCard" @select="selectCreditCard">
                    </ac-current-credit-cards>
                </div>
            </div>
        </template>
        <template slot="buttons">
            <div v-if="!updateCC">
                <button class="btn btn-red btn-sm" @click.prevent="closeModal">CANCEL PAYMENT</button>
                <button class="btn btn-green btn-sm" @click.prevent="processTransaction" :disabled="!memberAgrees">
                    COLLECT PAYMENT
                </button>
            </div>
        </template>
    </ac-modal>
</template>

<script>
    import {api} from "../../../global/framework/api.js";
    import AcButton from "../../../global/customHtmlElements/AcButton.vue";
    import AcCurrentCreditCards from "../../custom/AcCurrentCreditCards.vue";
    import {formatter} from "../../../global/formatter.js";
    import {common} from "../../../global/common.js";
    import {consumerPaymentMethodMixin} from "../../../components/shared/mixins/consumerPaymentMethodMixin.js";

    export default {
        name: "DebtReconciliationModal",
        components: {AcCurrentCreditCards, AcButton},
        mixins: [consumerPaymentMethodMixin],
        data() {
            return {                
                ccWidgetReady: true,
                netDebt: 0,
                debtRecords: [],
                member: '',
                memberAgrees: false,
                consumerId: 0,
                providerEntityId: 0,
                ccVm: {},
                updateCC: false,
                selectedCardDisplay: null,
                defaultCC: null,
                selectedCC: null,
            };
        },
        methods: {
            monthName(monthNum) {
                return common.getMonthName(monthNum);
            },
            async showModal({consumerId, providerEntityId}) {
                this.consumerId = consumerId;
                this.providerEntityId = providerEntityId;

                await this.getDebtAndCreditInfo();

                this.openModal();
            },
            async saveCreditCard() {
                let cardData = await this.tryGettingCardData();

                if (cardData) {
                    let url = `${this.providerEntityId}/SaveCreditCardInfo/${this.consumerId}`;
                    let result = await api.post('ProviderPayments', url, cardData.updatedCard);

                    if (result) {
                        await this.refresh();
                    }
                }
            },
            cancelCreditCardUpdate() {
                this.updateCC = false;
            },
            async processTransaction() {
                let records = this.debtRecords.filter(x => x.isChecked).map(y => y.consumerDebtId);
                let data = {
                    consumerId: this.consumerId, 
                    providerResolvingPayment: this.providerEntityId,
                    consumerDebtIds: records,
                    paymentMethodId: this.selectedCC !== null ? this.selectedCC.paymentMethodId : this.defaultCC.paymentMethodId
                }

                let result = await api.post('ProviderPayments', 'CollectDebt', data);

                if (result) {
                    this.closeModal();
                    this.$bus.$emit(this.$AcEvents.DEBT_HAS_BEEN_RESOLVED, result);
                }
            },
            async deleteCreditCard(data){
                let result = await api.post('ProviderPayments', `${this.providerEntityId}/DeleteCard/${this.consumerId}/${data.paymentMethodId}`);
                if (result) {
                    await this.refresh();
                }
            },        
            async defaultCreditCard(data){
                let result = await api.post('ProviderPayments', `${this.providerEntityId}/DefaultCard/${this.consumerId}/${data.paymentMethodId}`);
                if (result) {
                    await this.refresh();
                }
            },
            async selectCreditCard(paymentMethodId){
                this.selectedCC = this.ccVm.currentCards.find(x => x.paymentMethodId === paymentMethodId);
                this.updateCC = false;
                await this.getDebtAndCreditInfo();
            },
            async getDebtAndCreditInfo(){
                let [debtInfo, ccInfo] = await Promise.all([
                    api.getDataWithTwoIds('ProviderPayments', this.providerEntityId, 'GetDebtInfo', this.consumerId),
                    api.getDataWithTwoIds('ProviderPayments', this.providerEntityId, 'GetCreditCardInfoForPrimary', this.consumerId)
                ]);

                this.debtRecords = debtInfo.debtRecords.map(x => { x.isChecked = true; return x; });
                this.netDebt = debtInfo.netDebt;
                this.member = `${this.debtRecords[0].firstName} ${this.debtRecords[0].lastName}`;
                this.ccVm = ccInfo;
                let currCards = this.ccVm.currentCards;

                if (currCards === null) {
                    this.selectedCardDisplay = "There is no card on file currently.";
                }
                else {
                    this.defaultCC = currCards.find(x => x.isDefault);
                    if(this.selectedCC !== null){
                        this.selectedCardDisplay = `Selected Card: ${this.selectedCC.brand}, ${this.selectedCC.maskedCardNumber}, expiring on ${this.selectedCC.expirationMonth}/${this.selectedCC.expirationYear}`;
                    }
                    else {
                        this.selectedCardDisplay = `Default Card: ${this.defaultCC.brand}, ${this.defaultCC.maskedCardNumber}, expiring on ${this.defaultCC.expirationMonth}/${this.defaultCC.expirationYear}`;
                    }
                }
            },
            async refresh(){
                this.ccWidgetReady = false;
                await this.getDebtAndCreditInfo();
                this.ccWidgetReady = true;
            },
            openModal() {
                this.$refs.modal.open();
            },
            closeModal() {
                this.$refs.modal.close();
            },
        }
    }
</script>

<style scoped>

</style>